import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MainLayout from '../components/layout/MainLayout';
import WeatherCard from '../components/weather/WeatherCard';
import { resolveDashboardWeatherSites } from '../components/weather/resolveDashboardWeatherSites';
import { useAuth } from '../contexts/AuthContext';
import { useDealerAdmin } from '../contexts/DealerAdminContext';
import { ROUTES } from '../utils/constants';
import { dashboardQueryScope } from '../hooks/dashboardQueryScope';
import { useDashboardData } from '../hooks/useDashboardData';
import {
  readSupportedTenantSession,
  subscribeSupportedTenantSession,
} from './Admin/supportedTenantsSession';
import TrainingDashboardCard from '../components/training/TrainingDashboardCard';
import { fetchTrainingCurriculum } from '../services/trainingService';
import { dashboardAgentMetric } from '../services/dashboardDataService';

const ACTIVE_AGENT_HEALTH = new Set(['ONLINE', 'DEGRADED', 'HEALTHY', 'STALE']);
const ACTIVE_DEVICE_STATUS = new Set(['active', 'online', 'connected', 'ready', 'registered']);
const SIP_FAILED_STATUS = new Set(['failed', 'error', 'blocked', 'rejected', 'timeout']);
const WARNING_EVENT_STATUS = new Set(['failover', 'no_flow_bound', 'degraded', 'stale']);
const CRITICAL_EVENT_STATUS = new Set(['failed', 'error', 'timeout', 'critical', 'offline']);

const toTs = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isDeviceActive = (status) => ACTIVE_DEVICE_STATUS.has(String(status || '').trim().toLowerCase());

const getEventStatus = (event) => String(event?.status || event?.context?.status || '').trim().toLowerCase();

const getEventActor = (event) => (
  event?.actor?.userId || event?.actor?.agentId || event?.userId || event?.agentId || 'system'
);

const getActorLabel = (actor) => {
  const raw = String(actor || '').trim();
  if (!raw) return 'System';
  if (raw.toLowerCase() === 'system') return 'System';
  if (raw.startsWith('agent-')) return 'Agent';
  if (raw.includes('@')) return 'User';
  return raw.length > 18 ? 'Operator' : raw;
};

const getEventStatusLabel = (status) => {
  const norm = String(status || '').trim().toLowerCase();
  if (!norm) return 'unknown';
  if (norm === 'no_flow_bound') return 'No flow bound';
  if (norm === 'failover') return 'Agent failover';
  if (norm === 'offline') return 'Agent offline';
  return norm.replace(/_/g, ' ');
};

const getEventSummary = (event) => {
  const status = getEventStatus(event);
  const source = String(event?.source || event?.context?.source || '').toLowerCase();
  const type = String(event?.type || event?.eventType || '').toLowerCase();

  if (status === 'failover') return 'Failover active';
  if (status === 'no_flow_bound') return 'No flow assigned';
  if (status === 'offline') return 'Agent offline';
  if (CRITICAL_EVENT_STATUS.has(status) && source.includes('sip')) return 'Call delivery issue';
  if (CRITICAL_EVENT_STATUS.has(status) && source.includes('email')) return 'Email delivery issue';
  if (type === 'inbound') return 'Inbound communication received';
  if (type === 'outbound') return 'Outbound communication sent';
  if (status === 'success' || status === 'ok') return 'Operation completed';
  return getEventStatusLabel(status || type || 'status');
};

const getEventDescription = (event) => {
  const status = getEventStatus(event);
  if (status === 'failover') return 'Traffic has moved to a backup agent to keep service live.';
  if (status === 'no_flow_bound') return 'A trigger fired, but no response flow is assigned yet.';
  if (status === 'offline') return 'An agent is disconnected and not handling traffic.';
  if (status === 'failed' || status === 'error' || status === 'timeout') return 'The system detected a delivery or processing failure.';
  return 'Normal system activity.';
};

const getEventTone = (status) => {
  const norm = String(status || '').trim().toLowerCase();
  if (SIP_FAILED_STATUS.has(norm)) return 'error.main';
  if (WARNING_EVENT_STATUS.has(norm)) return 'warning.main';
  return 'success.main';
};

const getEventDetails = (event) => {
  const meta = event?.context?.meta || {};
  const context = event?.context || {};
  const parts = [];

  const triggerType = meta?.triggerType || event?.context?.trigger?.type;
  if (triggerType) parts.push(`trigger:${triggerType}`);

  const flowId = meta?.flowId || event?.context?.flowId || event?.flowId;
  if (flowId) parts.push(`flow:${flowId}`);

  const actionId = meta?.actionId || meta?.selectedActionId || event?.context?.actionId;
  if (actionId) parts.push(`action:${actionId}`);

  if (Array.isArray(meta?.actionIds) && meta.actionIds.length > 0) {
    parts.push(`actions:${meta.actionIds.length}`);
  }

  const route = event?.context?.route;
  if (route) parts.push(route);

  const source = event?.source || context?.source;
  if (source) parts.push(`src:${source}`);

  const severity = event?.severity || context?.severity;
  if (severity) parts.push(`sev:${String(severity).toLowerCase()}`);

  const correlationId = event?.correlationId || context?.correlationId || meta?.correlationId;
  if (correlationId) parts.push(`cid:${correlationId}`);

  const traceId = event?.traceId || context?.traceId || meta?.traceId;
  if (traceId) parts.push(`trace:${traceId}`);

  const provider = context?.provider || meta?.provider;
  if (provider) parts.push(`provider:${provider}`);

  const latencyMs = context?.latencyMs || meta?.latencyMs;
  if (Number.isFinite(Number(latencyMs))) parts.push(`latency:${Number(latencyMs)}ms`);

  const errorCode = context?.errorCode || meta?.errorCode;
  if (errorCode) parts.push(`code:${errorCode}`);

  const errorMessage = context?.error || context?.errorMessage || event?.error;
  if (errorMessage) parts.push(`err:${String(errorMessage).slice(0, 80)}`);

  const userId = event?.actor?.userId || meta?.userId || event?.context?.userId || event?.userId;
  if (userId) parts.push(`by:${userId}`);

  return parts.join(' • ') || '—';
};

const MetricCard = ({ title, primary, secondary, color }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      height: 112,
      minWidth: 0,
      borderRadius: 2,
      borderColor: 'rgba(15,23,42,0.12)',
      background: '#ffffff',
      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.12)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    }}
  >
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
        fontWeight: 700,
        letterSpacing: 0.35,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {title}
    </Typography>
    <Typography
      variant="h5"
      sx={{
        lineHeight: 1.1,
        fontWeight: 800,
        color: 'text.primary',
        fontSize: { xs: '1.55rem', md: '1.45rem', lg: '1.8rem' },
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {primary}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: '0.8rem',
      }}
      title={String(secondary || '')}
    >
      {secondary}
    </Typography>
  </Paper>
);

const DashboardPage = () => {
  const navigate = useNavigate();
  const { currentUser, tenantId: authTenantId, userClaims, userData } = useAuth();
  const dealerAdmin = useDealerAdmin() || {};
  const [supportSession, setSupportSession] = useState(() => readSupportedTenantSession());
  useEffect(() => subscribeSupportedTenantSession(() => {
    setSupportSession(readSupportedTenantSession());
  }), []);
  const dashboardScope = dashboardQueryScope({
    authTenantId,
    supportTenantId: supportSession?.tenantId || '',
    supportAccessLevel: supportSession?.accessLevel || '',
    dealerActive: Boolean(dealerAdmin.active),
    dealerId: dealerAdmin.dealerId || '',
    dealerTenantId: dealerAdmin.tenantId || '',
  });
  const tenantId = dashboardScope.tenantId;
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useDashboardData(tenantId, {
    scope: dashboardScope.scope,
    enabled: Boolean(currentUser && tenantId),
  });
  const dashboardReady = data?.tenantId === tenantId;

  const preferredSiteId = userClaims?.siteId || userData?.siteId || null;
  const userRole = userClaims?.role || userData?.role || '';
  const showTrainingDashboardCard = ['tenant_admin', 'admin', 'superadmin'].includes(userRole);
  const [trainingCurriculum, setTrainingCurriculum] = useState(null);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const weatherSitesInfo = useMemo(
    () => resolveDashboardWeatherSites(data?.sites, preferredSiteId),
    [data?.sites, preferredSiteId]
  );
  const [weatherSiteId, setWeatherSiteId] = useState(null);

  useEffect(() => {
    if (!weatherSitesInfo.sites.length) {
      setWeatherSiteId(null);
      return;
    }
    setWeatherSiteId((prev) => {
      if (prev && weatherSitesInfo.sites.some((s) => s.id === prev)) return prev;
      return weatherSitesInfo.siteId;
    });
  }, [weatherSitesInfo]);

  useEffect(() => {
    if (!showTrainingDashboardCard || !currentUser) {
      setTrainingCurriculum(null);
      return;
    }
    let cancelled = false;
    setTrainingLoading(true);
    fetchTrainingCurriculum()
      .then((resp) => {
        if (!cancelled) setTrainingCurriculum(resp?.data || resp);
      })
      .catch(() => {
        if (!cancelled) setTrainingCurriculum(null);
      })
      .finally(() => {
        if (!cancelled) setTrainingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showTrainingDashboardCard, currentUser]);

  const selectedWeatherSite = weatherSitesInfo.sites.find((s) => s.id === weatherSiteId) || null;

  const metrics = useMemo(() => {
    const sites = Array.isArray(data?.sites) ? data.sites : [];
    const users = Array.isArray(data?.users) ? data.users : [];
    const agents = Array.isArray(data?.agents) ? data.agents : [];
    const devices = Array.isArray(data?.devices) ? data.devices : [];
    const flows = Array.isArray(data?.flows) ? data.flows : [];
    const actions = Array.isArray(data?.actions) ? data.actions : [];
    const incomingEvents = Array.isArray(data?.incomingEvents) ? data.incomingEvents : [];
    const healthByAgentId = data?.agentHealth?.byAgentId || {};
    const pagingZones = Array.isArray(data?.pagingZones) ? data.pagingZones : [];

    const activeAgents = Object.values(healthByAgentId).filter((agent) => (
      ACTIVE_AGENT_HEALTH.has(String(agent?.healthStatus || '').toUpperCase())
    )).length;
    const agentMetric = dashboardAgentMetric(data?.agentsError, activeAgents, agents.length);
    const inactiveAgents = agentMetric.failed
      ? 0
      : Math.max(0, agents.length - activeAgents);

    const activeDevices = devices.filter((device) => isDeviceActive(device?.status)).length;
    const inactiveDevices = Math.max(0, devices.length - activeDevices);

    const actionsInFlows = new Set();
    flows.forEach((flow) => {
      if (Array.isArray(flow?.actions)) {
        flow.actions.forEach((actionRef) => {
          const actionId = actionRef?.actionId || actionRef?.id;
          if (actionId) actionsInFlows.add(actionId);
        });
      }
    });

    const unassignedActions = actions.filter((action) => {
      const id = action?.id || action?.actionId;
      return id && !actionsInFlows.has(id);
    }).length;

    const now = Date.now();
    const last24hSipEvents = incomingEvents.filter((event) => {
      const timestamp = toTs(event?.timestamp);
      const ageValid = timestamp ? (now - timestamp.getTime()) <= (24 * 60 * 60 * 1000) : false;
      const source = String(event?.source || event?.context?.source || '').toLowerCase();
      const type = String(event?.type || event?.eventType || '').toLowerCase();
      return ageValid && (source.includes('sip') || type.includes('sip'));
    });

    const sipFailed = last24hSipEvents.filter((event) => SIP_FAILED_STATUS.has(getEventStatus(event))).length;

    const recentActivity = [...incomingEvents]
      .sort((a, b) => (toTs(b?.timestamp)?.getTime() || 0) - (toTs(a?.timestamp)?.getTime() || 0))
      .slice(0, 10)
      .map((event) => ({
        id: event?.id || event?.eventId || `${event?.type || 'event'}-${event?.timestamp || Math.random()}`,
        time: toTs(event?.timestamp),
        actor: getActorLabel(getEventActor(event)),
        status: getEventStatus(event) || 'unknown',
        statusLabel: getEventStatusLabel(getEventStatus(event) || 'unknown'),
        statusTone: getEventTone(getEventStatus(event) || 'unknown'),
        summary: getEventSummary(event),
        details: getEventDescription(event),
      }));

    const failoverCount = recentActivity.filter((event) => event.status === 'failover').length;
    const noFlowCount = recentActivity.filter((event) => event.status === 'no_flow_bound').length;
    const criticalCount = recentActivity.filter((event) => CRITICAL_EVENT_STATUS.has(event.status)).length;

    const activeIssues = [];
    if (agentMetric.failed) {
      activeIssues.push({
        title: 'Agent list unavailable',
        detail: 'The agent list could not be loaded.',
        severity: 'warning',
      });
    } else if (inactiveAgents > 0) {
      activeIssues.push({
        title: 'Agent offline',
        detail: `${inactiveAgents} agent${inactiveAgents > 1 ? 's are' : ' is'} currently unavailable.`,
        severity: 'critical',
      });
    }
    if (failoverCount > 0) {
      activeIssues.push({
        title: 'Failover active',
        detail: 'Traffic is currently routed through backup capacity.',
        severity: 'warning',
      });
    }
    if (noFlowCount > 0) {
      activeIssues.push({
        title: 'Unassigned response flow',
        detail: 'Some trigger actions are missing a linked response flow.',
        severity: 'warning',
      });
    }
    if (sipFailed > 0) {
      activeIssues.push({
        title: 'Call delivery issues',
        detail: `${sipFailed} failed SIP event${sipFailed > 1 ? 's' : ''} in the last 24 hours.`,
        severity: 'critical',
      });
    }

    const systemStatus = inactiveAgents > 0 || criticalCount > 0
      ? 'Critical'
      : activeIssues.length > 0
        ? 'Degraded'
        : 'Healthy';

    const statusMessage = systemStatus === 'Critical'
      ? 'Immediate attention required. One or more core services are impacted.'
      : systemStatus === 'Degraded'
        ? 'Service is running with reduced resilience. Monitor active issues.'
        : 'All monitored services are operating normally.';

    return {
      counts: {
        sites: sites.length,
        users: users.length,
        agents: agents.length,
        devices: devices.length,
        flows: flows.length,
        actions: actions.length,
      },
      activeAgents,
      inactiveAgents,
      agentMetric,
      activeDevices,
      inactiveDevices,
      sipConfigured: Boolean(data?.sip?.configured),
      sipFailed,
      pagingZonesCount: pagingZones.length,
      pagingZonesSample: pagingZones.slice(0, 3).map((zone) => zone?.name || zone?.id).filter(Boolean),
      flowCount: flows.length,
      actionCount: actions.length,
      unassignedActions,
      recentActivity,
      activeIssues,
      systemStatus,
      statusMessage,
    };
  }, [data]);

  if (!currentUser) return <Navigate to={ROUTES.LOGIN} replace />;

  return (
    <MainLayout scrollMain>
      <Box sx={{ p: 2, minHeight: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: '#F3F4F6', color: 'text.primary' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h4" sx={{ lineHeight: 1.15, fontWeight: 400, color: 'text.primary' }}>{data?.tenant?.name || 'Tenant'} Dashboard</Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => refetch({ cancelRefetch: false })}
            disabled={isFetching}
            sx={{ color: 'text.primary', borderColor: 'rgba(15,23,42,0.25)' }}
            startIcon={isFetching ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
          >
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>

        {(isLoading || !dashboardReady) && !isError ? (
          <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">Loading dashboard metrics...</Typography>
          </Paper>
        ) : null}

        {isError ? (
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="body2" color="error">{error?.message || 'Failed to load dashboard data'}</Typography>
          </Paper>
        ) : null}

        {dashboardReady ? <Box
          sx={{
            display: 'grid',
            gap: 1.25,
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
              lg: 'repeat(7, minmax(0, 1fr))',
            },
          }}
        >
          <MetricCard title="Sites" primary={metrics.counts.sites} secondary="total" color="rgba(136, 141, 147, 0.85)" />
          <MetricCard title="Users" primary={metrics.counts.users} secondary="tenant users" color="rgba(136, 141, 147, 0.85)" />
          <MetricCard title="Agents" primary={metrics.agentMetric.primary} secondary={metrics.agentMetric.secondary} color={metrics.agentMetric.failed || metrics.inactiveAgents > 0 ? '#fca5a5' : '#86efac'} />
          <MetricCard title="Devices" primary={`${metrics.activeDevices} online`} secondary={`${metrics.inactiveDevices} offline`} color={metrics.inactiveDevices > 0 ? '#fca5a5' : '#86efac'} />
          <MetricCard title="SIP" primary={metrics.sipConfigured ? 'Ready' : 'Not Ready'} secondary={`${metrics.sipFailed} issue${metrics.sipFailed === 1 ? '' : 's'} in 24h`} color={metrics.sipFailed > 0 ? '#fca5a5' : '#86efac'} />
          <MetricCard title="Flows" primary={`${metrics.flowCount}`} secondary={`${metrics.unassignedActions} unassigned actions`} color={metrics.unassignedActions > 0 ? '#fcd34d' : 'rgba(226,232,240,0.85)'} />
          <MetricCard
            title="Actions"
            primary={`${metrics.actionCount}`}
            secondary={`${Math.max(0, metrics.actionCount - metrics.unassignedActions)} assigned to flows`}
            color={metrics.unassignedActions > 0 ? '#fcd34d' : '#86efac'}
          />
        </Box> : null}

        {showTrainingDashboardCard ? (
          <TrainingDashboardCard curriculum={trainingCurriculum} loading={trainingLoading} />
        ) : null}

        <Paper
          variant="outlined"
          sx={{
            p: 1.25,
            borderRadius: 2,
            bgcolor: '#ffffff',
            color: 'text.primary',
            borderColor: 'rgba(15,23,42,0.12)',
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)',
          }}
          data-testid="dashboard-weather-panel"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: weatherSitesInfo.sites.length ? 1 : 0,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 750 }}>
              Site weather
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {weatherSitesInfo.sites.length > 1 ? (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="dashboard-weather-site-label">Site</InputLabel>
                  <Select
                    labelId="dashboard-weather-site-label"
                    label="Site"
                    value={weatherSiteId || ''}
                    onChange={(event) => setWeatherSiteId(event.target.value)}
                    inputProps={{ 'data-testid': 'dashboard-weather-site-select' }}
                  >
                    {weatherSitesInfo.sites.map((site) => (
                      <MenuItem key={site.id} value={site.id}>
                        {site.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              {weatherSiteId ? (
                <Button
                  size="small"
                  variant="outlined"
                  data-testid="dashboard-open-ambient"
                  onClick={() =>
                    navigate(`${ROUTES.AMBIENT}?siteId=${encodeURIComponent(weatherSiteId)}`, {
                      state: { returnTo: ROUTES.DASHBOARD },
                    })
                  }
                >
                  Screen Saver
                </Button>
              ) : null}
            </Box>
          </Box>

          {weatherSitesInfo.sites.length === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid="dashboard-weather-no-sites">
              No sites available for weather.
            </Typography>
          ) : (
            <WeatherCard
              siteId={weatherSiteId}
              siteName={selectedWeatherSite?.name}
            />
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.25, flex: 1, minHeight: 0, borderRadius: 2, bgcolor: '#ffffff', color: 'text.primary' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 750 }}>Recent Activity</Typography>
          </Box>
          <Divider sx={{ mb: 0.5 }} />
          <Box sx={{ maxHeight: 260, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {metrics.recentActivity.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>{event.time ? event.time.toLocaleTimeString() : '—'}</TableCell>
                    <TableCell>{event.summary}</TableCell>
                    <TableCell>{event.actor}</TableCell>
                    <TableCell>
                      <Box component="span" sx={{ color: event.statusTone }}>
                        {event.statusLabel}
                      </Box>
                    </TableCell>
                    <TableCell>{event.details}</TableCell>
                  </TableRow>
                ))}
                {metrics.recentActivity.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>No recent activity</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>
    </MainLayout>
  );
};

export default DashboardPage;
